import type { ChosenVisibility, StoryVisibility } from '@/infrastructure/api/v1/mappers/stories';

export type PublishDevice = 'app' | 'appless' | 'spectacles';

// Where a finished story is sent. 'audience' publishes to the device platforms;
// 'template' submits a copy of the version to the template gallery.
export type PublishDestination = 'audience' | 'template';

export type StepId =
  | 'choose-platform'
  | 'project-info'
  | 'scene-info'
  | 'anchor-info'
  | 'confirmation'
  | 'template-info'
  | 'template-features'
  | 'template-media'
  | 'template-confirm';

export type AnchorCategory =
  | 'place_anywhere'
  | 'immersal'
  | 'image_anchor'
  | 'geospatial_anchor';

export interface DeviceAvailability {
  state: 'selectable' | 'disabled';
  disabledReason?: string;
}

export type PublishVersionStatus =
  | 'draft'
  | 'live'
  | 'archived'
  | 'pending'
  | 'locked'
  | 'template';

export interface PublishVersionOption {
  sceneInstanceId: string;
  label: string;
  status: PublishVersionStatus;
  versionNumber?: number;
  // Only draft/template versions can be submitted for review. Non-publishable
  // versions are still listed (greyed out) so the creator sees the full picture.
  selectable: boolean;
  disabledReason?: string;
  // Scene instance anchor category. 'place_anywhere' scenes have no anchor and
  // can't be published; undefined when the source didn't supply it.
  anchorCategory?: AnchorCategory;
}

export interface PublishFlowSubmitArgs {
  destination: PublishDestination;
  devices: PublishDevice[];
  // The version the creator chose to publish. Defaults to the entry point's
  // current scene instance when there is only one publishable version.
  sceneInstanceId?: string;
  // Edited metadata. A field is present only when its step was visible in the flow,
  // so consumers never overwrite a value the user had no opportunity to edit.
  storyName?: string;
  creatorName?: string;
  projectName?: string;
  projectDescription?: string;
  projectTags?: string[];
  sceneIntroduction?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  city?: string;
  // Audience destination only
  visibility?: ChosenVisibility;
  // Template destination only. These are written to the template copy, never back
  // to the story: the story keeps its own name and audience-facing text.
  templateName?: string;
  templateDescription?: string;
  templateLearnings?: string;
  templateCategory?: string;
  templateDifficulty?: TemplateDifficulty;
  templateTags?: string[];
  templateFeatures?: string[];
}

export type TemplateDifficulty = 'beginner' | 'intermediate' | 'advanced';

export type ProjectStepId =
  | 'select-scenes'
  | 'choose-platform'
  | 'project-info'
  | 'stories'
  | 'confirmation';

export interface ProjectSceneTarget {
  sceneOriginId: string;
  sceneOriginName: string;
  orderNumber: number;
  coverImageUrl?: string;
  visibility?: StoryVisibility;
  versions: PublishVersionOption[];
}

export interface ProjectSceneSubmit {
  sceneOriginId: string;
  sceneInstanceId: string;
  sceneOriginName: string;
  storyName?: string;
  sceneIntroduction?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  city?: string;
}

export interface ProjectPublishSubmitArgs {
  devices: PublishDevice[];
  // Asked once, applied to every story submitted
  visibility?: ChosenVisibility;
  creatorName?: string;
  projectName?: string;
  projectDescription?: string;
  projectTags?: string[];
  scenes: ProjectSceneSubmit[];
}

// Thrown by an onSubmit handler when required-field validation fails. The failure
// is already surfaced to the user (e.g. the Missing Required Information dialog),
// so the modal treats it as a non-success: stay open, no success screen, no
// generic error banner.
export class PublishValidationError extends Error {
  constructor() {
    super('Publish validation failed');
    this.name = 'PublishValidationError';
  }
}
